/**
 * Tabela de destinatários moderna para campanhas
 * - Filtros rápidos por status (pílulas)
 * - Coluna Inbox de disparo (servidor já resolve o nome)
 * - Status sem Badge: somente ícone colorido + texto preto
 * - Linha clicável: abre modal de chat na inbox em que foi disparado
 */

import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { PaginationFooter } from '@/components/ui/pagination-footer';
import {
  Search, Clock, Send, CheckCheck, Eye, XCircle,
  AlertTriangle, User, Server, MessageSquare, Loader2
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { getAvatarColorClasses } from '@/lib/utils/avatar-colors';

// Tipo do destinatário enriquecido pela RPC get_campaign_recipients
// (inclui inbox_name e total_count adicionados na nova assinatura)
export interface RecipientRow {
  id: string;
  campaign_id: string;
  contact_id: string | null;
  contact_name: string | null;
  whatsapp_name?: string | null;
  phone: string;
  status: string;
  has_whatsapp: boolean | null;
  sent_via_inbox_id: string | null;
  inbox_name: string | null;
  external_message_id: string | null;
  message_id: string | null;
  sent_at: string | null;
  delivered_at: string | null;
  read_at: string | null;
  error_message: string | null;
  sort_order: number | null;
  created_at: string | null;
  total_count?: number;
}

interface RecipientTableProps {
  recipients: RecipientRow[];
  /** Estatísticas para os contadores das pílulas (vem do parent) */
  stats: {
    total: number;
    pending: number;
    sent: number;
    delivered: number;
    read: number;
    failed: number;
    skipped: number;
  };
  /** Status selecionado nas pílulas (controlado pelo parent) */
  statusFilter: string | null;
  onStatusFilterChange: (status: string | null) => void;
  /** Busca controlada pelo parent (para enviar à RPC server-side) */
  search: string;
  onSearchChange: (value: string) => void;
  /** Clique em destinatário → abrir chat na inbox de disparo */
  onRecipientClick?: (recipient: RecipientRow) => void;
  /** Estado de loading enquanto a RPC roda */
  loading?: boolean;
  /** Paginação server-side (v1.2) */
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  /** Slot opcional renderizado à direita das pílulas (ex.: botão de exportação) */
  exportSlot?: React.ReactNode;
}


// Configuração visual de status: ícone colorido + texto preto (sem badge)
const statusConfig: Record<string, {
  label: string;
  icon: React.ReactNode;
}> = {
  pending: {
    label: 'Pendente',
    icon: <Clock className="h-3.5 w-3.5 text-muted-foreground" />,
  },
  processing: {
    label: 'Processando',
    icon: <Clock className="h-3.5 w-3.5 text-orange-600 animate-spin" />,
  },
  sent: {
    label: 'Enviado',
    icon: <Send className="h-3.5 w-3.5 text-blue-600" />,
  },
  delivered: {
    label: 'Entregue',
    icon: <CheckCheck className="h-3.5 w-3.5 text-green-600" />,
  },
  read: {
    label: 'Lido',
    icon: <Eye className="h-3.5 w-3.5 text-green-600" />,
  },
  failed: {
    label: 'Falhou',
    icon: <XCircle className="h-3.5 w-3.5 text-destructive" />,
  },
  skipped: {
    label: 'Ignorado',
    icon: <AlertTriangle className="h-3.5 w-3.5 text-yellow-600" />,
  },
};

// Pílulas de filtro rápido
const quickFilters: Array<{ key: string | null; label: string; statKey?: keyof RecipientTableProps['stats'] }> = [
  { key: null, label: 'Todos', statKey: 'total' },
  { key: 'pending', label: 'Pendentes', statKey: 'pending' },
  { key: 'sent', label: 'Enviados', statKey: 'sent' },
  { key: 'delivered', label: 'Entregues', statKey: 'delivered' },
  { key: 'read', label: 'Lidos', statKey: 'read' },
  { key: 'failed', label: 'Falhas', statKey: 'failed' },
  { key: 'skipped', label: 'Ignorados', statKey: 'skipped' },
];

function getInitials(name: string | null): string {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

export function RecipientTable({
  recipients,
  stats,
  statusFilter,
  onStatusFilterChange,
  search,
  onSearchChange,
  onRecipientClick,
  loading,
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  exportSlot,
}: RecipientTableProps) {
  // Numeração da coluna "#" respeita a página atual
  const startIndex = (page - 1) * pageSize;


  return (
    <div className="rounded-xl border bg-card overflow-hidden">
      {/* Header: busca + filtros rápidos por status */}
      <div className="p-3 border-b bg-muted/30 space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nome ou telefone..."
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-10 bg-background"
          />
        </div>

        {/* Pílulas de filtro rápido por status + slot de exportação à direita */}
        <div className="flex flex-wrap items-center gap-2">
          {quickFilters.map((f) => {
            const active = statusFilter === f.key;
            const count = f.statKey ? stats[f.statKey] : 0;
            return (
              <Button
                key={f.label}
                size="xs"
                variant={active ? 'default' : 'outline'}
                onClick={() => onStatusFilterChange(f.key)}
                className="gap-1.5 h-7"
              >
                <span>{f.label}</span>
                <span
                  className={cn(
                    'text-[10px] px-1.5 py-0.5 rounded-full',
                    active ? 'bg-primary-foreground/20 text-primary-foreground' : 'bg-muted text-foreground'
                  )}
                >
                  {count}
                </span>
              </Button>
            );
          })}

          {exportSlot && <div className="ml-auto">{exportSlot}</div>}
        </div>

      </div>

      {/* Tabela */}
      <ScrollArea className="h-[420px]">
        <table className="w-full">
          <thead className="sticky top-0 bg-card border-b z-10">
            <tr>
              <th className="text-left p-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">#</th>
              <th className="text-left p-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Contato</th>
              <th className="text-left p-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Telefone</th>
              <th className="text-left p-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Inbox</th>
              <th className="text-left p-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Status</th>
              <th className="text-left p-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Enviado em</th>
              <th className="text-left p-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Erro</th>
            </tr>
          </thead>
          <tbody>
            {loading && recipients.length === 0 ? (
              <tr>
                <td colSpan={7} className="p-8 text-center text-muted-foreground">
                  <Loader2 className="h-5 w-5 mx-auto animate-spin" />
                </td>
              </tr>
            ) : recipients.length === 0 ? (
              <tr>
                <td colSpan={7} className="p-8 text-center text-muted-foreground">
                  <User className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>Nenhum destinatário encontrado</p>
                </td>
              </tr>
            ) : (
              recipients.map((recipient, index) => {
                const status = statusConfig[recipient.status || 'pending'] || statusConfig.pending;
                // Permite abrir o chat se a mensagem foi disparada (tem inbox + telefone).
                // O contact_id é resolvido server-side via RPC quando estiver NULL (campanhas legadas).
                const canOpenChat = !!(onRecipientClick && recipient.sent_via_inbox_id && (recipient.contact_id || recipient.phone));
                return (
                  <tr
                    key={recipient.id}
                    onClick={() => canOpenChat && onRecipientClick?.(recipient)}
                    title={canOpenChat ? 'Abrir conversa' : 'Destinatário ainda não foi disparado'}
                    className={cn(
                      'border-b last:border-0 transition-colors duration-200',
                      'hover:bg-muted/50',
                      index % 2 === 0 ? 'bg-background' : 'bg-muted/20',
                      canOpenChat ? 'cursor-pointer' : 'cursor-not-allowed opacity-90'
                    )}
                  >
                    <td className="p-3 text-sm text-muted-foreground font-medium">{startIndex + index + 1}</td>
                    <td className="p-3">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-8 w-8">
                          <AvatarFallback className={cn('text-xs font-medium', getAvatarColorClasses(recipient.contact_name))}>
                            {getInitials(recipient.contact_name)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="font-medium text-sm flex items-center gap-1.5">
                          {recipient.contact_name || (
                            <span className="text-muted-foreground italic">Sem nome</span>
                          )}
                          {canOpenChat && (
                            <MessageSquare className="h-3 w-3 text-muted-foreground/60" />
                          )}
                        </span>
                      </div>
                    </td>
                    <td className="p-3 text-sm font-mono text-muted-foreground">{recipient.phone}</td>
                    <td className="p-3">
                      {recipient.inbox_name ? (
                        <div className="flex items-center gap-1.5 text-sm text-foreground">
                          <Server className="h-3.5 w-3.5 text-emerald-600" />
                          <span className="truncate max-w-[180px]">{recipient.inbox_name}</span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-sm">—</span>
                      )}
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-1.5">
                        {status.icon}
                        <span className="text-sm text-foreground">{status.label}</span>
                      </div>
                    </td>
                    <td className="p-3 text-sm text-muted-foreground">
                      {recipient.sent_at
                        ? format(new Date(recipient.sent_at), 'dd/MM HH:mm', { locale: ptBR })
                        : '-'}
                    </td>
                    <td className="p-3">
                      {recipient.error_message ? (
                        <span
                          className="text-sm text-destructive truncate max-w-[200px] block"
                          title={recipient.error_message}
                        >
                          {recipient.error_message}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </ScrollArea>

      <PaginationFooter
        totalItems={total}
        currentPage={page}
        pageSize={pageSize}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        pageSizeOptions={[30, 50, 100]}
      />
    </div>
  );
}
